/* eslint-disable no-console */
import {
  differenceInYears,
  format,
  parse,
  startOfYear,
  subYears,
} from 'date-fns';
import {
  Contact,
  CurrentStep,
  Field,
  Goals,
  Profile,
  What,
  Who,
} from '../core/types';
import config from '../core/context/config/config';
import { validations } from './fieldValidations';

export const hasAllRequiredFields = (
  data: Who | What | Goals | Contact,
  currentStep: CurrentStep,
): boolean => {
  switch (currentStep) {
    case 'who':
      // TODO verify
      const { name, dateOfBirth, socialStatus } = data as Who;
      return !!(name && dateOfBirth && socialStatus);
    case 'what':
      const { balanceSheet, cashFlow } = data as What;
      return !!(
        balanceSheet?.bankSavingsValue &&
        balanceSheet.bankInvestmentsValue &&
        balanceSheet.insuranceProtectionValue &&
        balanceSheet?.businessAssetsValue &&
        balanceSheet?.homeValue &&
        balanceSheet.otherRealEstateValue &&
        balanceSheet.liabilitiesValue
      );
    case 'goals':
      // TODO verify
      const { retirement } = data as Goals;
      return !!retirement?.desiredEndCapitalValue;
    case 'contact':
      const contactData = data as Contact;
      return (
        !!contactData.name &&
        !!contactData.email &&
        !!contactData.mobile &&
        !!contactData.signedTermsAndConditions
      );
    default:
  }
  return false;
};

/* eslint-disable no-console */
export const formatValuta = (
  amount: number,
  valuta: string = '€',
  valutaPosition: 'before' | 'after' = 'before',
  amountOfDecimals: number = 2,
  thousandsSeparator: string = ' ',
) => {
  if (!amount && amount !== 0) return '';

  return `${valutaPosition === 'before' ? `${valuta} ` : ''}${amount
    .toFixed(amountOfDecimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)}${
    valutaPosition === 'after' ? ` ${valuta}` : ''
  }`;
};

export default formatValuta;

export const getActiva = (what: What): number => {
  if (!what.balanceSheet) {
    what.balanceSheet = {};
  }
  const balanceSheetActiva: number =
    (what.balanceSheet.bankInvestmentsValue ?? 0) +
    (what.balanceSheet.bankSavingsValue ?? 0) +
    (what.balanceSheet.insuranceProtectionValue ?? 0) +
    (what.balanceSheet.businessAssetsValue ?? 0) +
    (what.balanceSheet.homeValue ?? 0) +
    (what.balanceSheet.otherRealEstateValue ?? 0);
  const cashFlowActiva: number = what.cashFlow?.monthlyRevenuesValue ?? 0;

  const activa = balanceSheetActiva + cashFlowActiva;
  return activa;
};

export const getPassiva = (what: What): number => {
  if (!what.balanceSheet) {
    what.balanceSheet = {};
  }
  if (!what.cashFlow) {
    what.cashFlow = {};
  }
  const balanceSheetPassiva: number = what.balanceSheet.liabilitiesValue ?? 0;
  const cashFlowPassiva: number = what.cashFlow
    ? what.cashFlow.monthlyExpensesValue ?? 0
    : 0;
  const passiva = balanceSheetPassiva + cashFlowPassiva;
  return passiva;
};

export const getAgeFromDateString = (
  dateOfBirth?: string,
): number | undefined => {
  if (!dateOfBirth) return undefined;
  const date = parse(dateOfBirth, 'yyyy-MM-dd', new Date());
  return differenceInYears(new Date(), date);
};

export const getDateStringFromAge = (age: string): string => {
  return format(subYears(startOfYear(new Date()), Number(age)), 'yyyy-MM-dd');
};

export const getCurrentStep = (profile: Profile): CurrentStep => {
  if (profile.result || profile.contact.isCompleted) {
    return 'result';
  }
  if (profile.goals.isCompleted) {
    return 'contact';
  }

  if (profile.what.isCompleted) {
    return 'goals';
  }

  if (profile.who.isCompleted) {
    return 'what';
  }

  // as soon as we have persisted a single value on who
  if (profile.who) {
    return 'who';
  }

  return 'home';
};

// we use this to handle fetching images when the app is located on a path (eg. /leads/bsure)
export const addBaseName = (src: string) =>
  window.location.href.indexOf('localhost') > -1
    ? src
    : `${config.baseName}${src}`;

/* eslint-disable no-useless-escape */
export const isValidEmail = (email: string) => {
  // eslint-disable-next-line max-len
  const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const fieldHasError = (step: any, field: Field, value: any) => {
  const fieldValidations: any[] = validations[field];

  if (!fieldValidations) {
    return false;
  }

  return fieldValidations.reduce(
    (acc: any, { validation: fieldIsValid, msg }: any) => {
      if (!fieldIsValid(value)) {
        return true;
      }
      return acc;
    },
    false,
  );
};

export const mapLiquidAssetsGraph = (arr: any) => {
  const result = arr.reduce(
    (acc: any, cur: any) => {
      return {
        labels: [...acc.labels, cur[0]],
        data: [...acc.data, cur[1]],
      };
    },
    {
      labels: [],
      data: [],
    },
  );
  return result;
};

export const getSavedEmail = () => {
  const savedEmail = localStorage.getItem('savedEmail');
  if (savedEmail != null) {
    return savedEmail;
  }
  return undefined;
};

function getAPIEndpoint() {
  let backendHost;
  const apiVersion = 'v1';

  const hostname = window && window.location && window.location.hostname;

  if (/localhost/.test(hostname) || /stage/.test(hostname)) {
    backendHost = 'https://app.stage.myfaro.be';
  } else {
    backendHost = 'https://app.myfaro.be';
  }

  return `${backendHost}/api/${apiVersion}`;
}

export const API_ROOT = getAPIEndpoint();

export * from './themeSelector';
export * from './fieldValidations';
